<template>
  <div class="row poppins h-100">
    <div class="col-12 h-100">
      <div class="table-wrapper h-100 d-flex flex-column">
        <div class="d-flex justify-content-end">
          <b-button v-if="!isRootFolder" variant="outline-primary" size="lg" class="pl-4 pr-4 text-blue" v-b-modal.moveStructureModal>
            <BIconFolderSymlink class="mr-1" />
            {{ 'Move' | i18n }}
          </b-button>
          <MoveStructureModal
            :selected-structure="selectedStructure"
            :find-and-change-item="findAndChangeItem"
            :get-full-structure="getStructure"
            :root-folder-data="rootFolderData"
          />
          <b-button
            v-if="selectedStructure.type === 'file'"
            variant="outline-danger"
            size="lg"
            class="pl-4 pr-4 ml-4 height-44"
            @click="deleteStructureData"
          >
            <img :src="deleteIconUrl" class="mr-2">
            {{ 'Remove' | i18n }}
          </b-button>
          <b-button v-else v-b-modal.addStructureModal variant="primary" size="lg" class="pl-3 pr-4 ml-4 height-44">
            <BIconPlus font-scale="1.5" />
            {{ 'Data' | i18n }}
          </b-button>
          <AddStructureModal
            :parent-folder-id="selectedStructure.id"
            :parent-child-list-length="selectedStructure.childList ? selectedStructure.childList.length : 0"
            :update-general-structure="updateGeneralStructure"
            :get-full-structure="getStructure"
          />
        </div>
        <div class="d-flex mt-3 h-100 overflow-hidden">
          <div class="w-25 d-flex flex-column overflow-hidden">
            <SearchInput :search-change="onSearchHandler" class="pr-4" />
            <div class="d-flex align-items-center mt-2 pr-4">
              <p class="visible-view-desc mb-0 mr-1">
                {{ 'Active:' | i18n }}
              </p>
              <b-dropdown :text="$translate(visibleView)" size="sm" variant="text">
                <b-dropdown-item @click="() => visibleView = VIEW_ALL">
                  {{ VIEW_ALL | i18n }}
                </b-dropdown-item>
                <b-dropdown-item @click="() => visibleView = VIEW_ACTIVE">
                  {{ VIEW_ACTIVE | i18n }}
                </b-dropdown-item>
              </b-dropdown>
            </div>
            <div class="h-100 mt-1 folder-structure-wrapper pr-4 overflow-auto">
              <FolderStructureTree
                v-for="value in currentStructure"
                :key="value.id"
                :value="value"
                :on-change-tree-handler="onChangeTreeHandler"
                :selected-structure="selectedStructure"
                :on-selected-structure="onSelectedStructure"
              />
            </div>
          </div>
          <div v-if="selectedStructure.type !== 'file'" class="w-75 details-wrapper">
            <div class="d-flex w-100 justify-content-between align-items-start mb-4">
              <div class="w-50">
                <BlockHeader name="Folder details" class="mt-0 mb-0" />
                <p v-if="!isRootFolder" class="description-text">
                  {{ 'Change/edit folder information' | i18n }}
                </p>
              </div>
              <b-button v-if="!isRootFolder" variant="primary" size="lg" class="px-5 py-1 w-225" @click="updateStructureData">
                {{ 'Save' | i18n }}
              </b-button>
            </div>
            <Input
              v-model="selectedStructure.name"
              form-group-id="folderName"
              placeholder="Folder name"
              :bottom="20"
              :view-mode="isRootFolder"
            />
            <Radio
              :value="selectedStructure.isActive ? 1 : 0"
              :on-change="() => selectedStructure.isActive = !selectedStructure.isActive"
              placeholder="Active"
              :options="activeOptions"
              small
              form-group-id="activeStatus"
              :view-mode="isRootFolder"
            />
            <div v-if="!isRootFolder" class="d-flex w-100 justify-content-between align-items-start mt-n4">
              <div class="w-50">
                <BlockHeader name="Removal" class="mt-0 mb-0" />
                <p class="description-text">
                  {{ 'Remove folder and all included sub-folders' | i18n }}
                </p>
              </div>
              <b-button variant="outline-danger" size="lg" class="px-5 py-1 w-225" @click="deleteStructureData">
                <img :src="deleteIconUrl" class="mr-2">
                {{ 'Remove' | i18n }}
              </b-button>
            </div>
          </div>
          <div v-else class="w-75 details-wrapper">
            <img
              v-if="selectedStructure.fileType.includes('image')"
              :src="getFileURL()"
              width="100%"
            >
            <embed
              v-else-if="selectedStructure.fileType.includes('pdf')"
              :src="getFileURL()"
              width="100%"
              height="100%"
              :type="selectedStructure.fileType"
            >
            <div v-else>
              <p>{{ 'File' | i18n }}: {{ selectedStructure.name }}</p>
              <b-button variant="outline-secondary" size="lg" class="px-5 py-1 w-225" @click="getFile">
                {{ 'download' | i18n }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { BIconPlus, BIconFolderSymlink } from 'bootstrap-vue';

import SearchInput from '../../common/search-input';
import BlockHeader from '../../common/block-header';
import Input from '../../common/input';
import Radio from '../../common/radio';
import deleteIconUrl from '../../../../assets/icons/delete.svg';
import api from '../../../api';
import { DeleteRowMixin, NotificationMixin, AuthMixin } from '../../../../mixins';
import FolderStructureTree from './folder-structure-tree';
import AddStructureModal from './add-structure-modal';
import MoveStructureModal from './move-structure-modal';

const VIEW_ACTIVE = 'Active';
const VIEW_ALL = 'All';

export default {
  name: 'ViewStructure',
  components: {
    BIconPlus,
    BIconFolderSymlink,
    SearchInput,
    BlockHeader,
    Input,
    Radio,
    FolderStructureTree,
    AddStructureModal,
    MoveStructureModal,
  },
  mixins: [DeleteRowMixin, NotificationMixin, AuthMixin],
  data() {
    return {
      activeOptions: [
        { value: 1, text: this.$translate('yes') },
        { value: 0, text: this.$translate('no') },
      ],
      deleteIconUrl,
      searchQuery: '',
      generalStructure: [],
      currentStructure: [],
      selectedStructure: {},
      rootFolderData: {
        childList: [],
        id: null,
        isActive: true,
        isOpen: true,
        name: this.$translate('Root folder'),
        nestingCount: -1,
        type: 'rootFolder',
      },
      accessToken: '',
      localBasePath: axios.defaults.baseURL,
      visibleView: VIEW_ALL,
      VIEW_ACTIVE,
      VIEW_ALL,
    };
  },
  computed: {
    isRootFolder() {
      return this.rootFolderData.id === this.selectedStructure.id;
    },
  },
  watch: {
    visibleView: {
      handler: async function() {
        await this.getStructure();
      },
      deep: true,
    },
    generalStructure: {
      handler: function() {
        if (this.searchQuery) {
          this.onSearchHandler({ target: { value: this.searchQuery } });
        }
      },
      deep: true,
    },
  },
  async created() {
    const auth = this.authStorageService.getAuth();
    this.accessToken = auth.access_token_link;
    await this.getStructure();
  },
  methods: {
    async getStructure() {
      const currentStructure = this.visibleView === VIEW_ACTIVE
        ? await api.viewStructure.getStructure()
        : await api.viewStructure.getFullStructure();
      this.generalStructure = currentStructure.resultList;
      this.currentStructure = [{ ...this.rootFolderData, childList: currentStructure.resultList }];
      this.selectedStructure = { ...this.rootFolderData };
    },
    findAndChangeItem({ array, condition, newValues, newItem = null }) {
      return array.map((item) => {
        if (condition(item)) {
          const currentChildList = newItem ? [...item.childList, newItem] : [...item.childList];
          return { ...item, ...newValues, childList: currentChildList };
        } else if (Array.isArray(item.childList)) {
          return { ...item, childList: this.findAndChangeItem({ array: item.childList, condition, newValues, newItem }) };
        } else {
          return item;
        }
      });
    },
    findAndDeleteItem({ array, condition }) {
      return array.reduce((acc, item) => {
        if (condition(item)) {
          return acc;
        } else {
          const newItem = { ...item };
          if (Array.isArray(item.childList)) {
            newItem.childList = this.findAndDeleteItem({ array: item.childList, condition });
          }
          acc.push(newItem);
          return acc;
        }
      }, []);
    },
    onChangeTreeHandler(newValue) {
      const newCurrentStructure = this.findAndChangeItem({
        array: this.currentStructure,
        condition: ({ id }) => id === newValue.id,
        newValues: { isOpen: !newValue.isOpen },
      });
      this.currentStructure = newCurrentStructure;
    },
    onSelectedStructure(newStructure) {
      this.selectedStructure = { ...newStructure };
    },
    async updateStructureData() {
      try {
        const params = {
          name: this.selectedStructure.name,
          owned: this.selectedStructure.owned,
          isActive: this.selectedStructure.isActive,
        };

        const updatedStructureData = await api.viewStructure.updateStructure({
          id: this.selectedStructure.id,
          params,
        });

        const newCurrentStructure = this.findAndChangeItem({
          array: this.currentStructure,
          condition: ({ id }) => id === updatedStructureData.id,
          newValues: updatedStructureData,
        });
        this.currentStructure = newCurrentStructure;
        this.showNotification({ type: 'success', message: 'Folder has been successfully updated!' });
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
      }
    },
    async deleteStructureData() {
      try {
        await this.deleteSelectedRows(
          {},
          async () => {
            const deletedId = this.selectedStructure.id;
            await api.viewStructure.deleteStructure(deletedId);

            const newCurrentStructure = this.findAndDeleteItem({
              array: this.currentStructure,
              condition: ({ id }) => id === deletedId,
            });
            this.currentStructure = newCurrentStructure;
            this.selectedStructure = { ...this.rootFolderData };
          },
          true,
          'This folder will be deleted',
        );
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
      }
    },
    updateGeneralStructure(createdFolderData) {
      const newCurrentStructure = this.findAndChangeItem({
        array: this.currentStructure,
        condition: ({ id }) => id === this.selectedStructure.id,
        newItem: { ...createdFolderData, isOpen: false, nestingCount: this.selectedStructure.nestingCount + 1, childList: [] },
      });
      this.currentStructure = newCurrentStructure;
      this.selectedStructure.childList = [...this.selectedStructure.childList, createdFolderData];
    },
    getFileURL() {
      return `${this.localBasePath}/api/v1/view/structure/${this.selectedStructure.fileHash}/file?authorization=${this.accessToken}`;
    },
    async getFile() {
      try {
        await axios(this.getFileURL());
        window.open(this.getFileURL(), '_blank');
      } catch (error) {
        this.showNotification({ type: 'error', message: this.$translate('file_do_not_uploaded') });
      }
    },
    recursiveSearch(data, searchText) {
      const results = [];
      for (const item of data) {
        if (item.name.toLowerCase().includes(searchText.toLowerCase())) {
          results.push({ ...item, childList: this.recursiveSearch(item.childList || [], searchText) });
        } else if (item.childList && item.childList.length > 0) {
          const foundInChild = this.recursiveSearch(item.childList, searchText);
          if (foundInChild.length > 0) {
            results.push({ ...item, childList: foundInChild });
          }
        }
      }
      return results;
    },
    onSearchHandler(e) {
      const searchText = e.target.value;
      this.searchQuery = searchText;
      const searchResult = this.recursiveSearch(this.generalStructure, searchText);
      this.currentStructure = [{ ...this.rootFolderData, childList: searchResult }];
    },
  },
};
</script>

<style scoped lang="scss">
.text-blue {
  color: #2260FF;
}
.height-44 {
  height: 44px;
}
.details-wrapper {
  border: 1px solid #C5D0DE66;
  padding: 20px;
  border-radius: 4px;
}
.description-text {
  color: #A4A5AC;
  font-family: 'Poppins';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  margin: 0;
}
.visible-view-desc {
  color: #A4A5AC;
  font-family: 'Poppins';
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: 0.56px;
}
.folder-structure-wrapper > div {
  background-color: #f9fafb;
  border-radius: 4px;
  min-height: 100%;
}
.folder-structure-wrapper::-webkit-scrollbar {
  width: 0px; /* Remove scrollbar space */
  background: transparent; /* Optional: just make scrollbar invisible */
}
.w-225 {
  width: 225px;
}
::v-deep .btn-lg {
  font-size: 16px;
  line-height: 24px;
}
::v-deep .btn-group > .btn:focus {
  box-shadow: none;
}
</style>
